/**
 * OpenSeaMap SEAMARK overlay — the free OSM-based nautical layer (buoys, lights,
 * beacons, harbours) served as transparent raster tiles meant to sit ON TOP of a
 * base. It's an OVERLAY (its own toggle), not a chart provider: it composes over
 * whichever base is showing — satellite imagery, a raster scan, or the vector
 * chart — and slots UNDER our own symbol and vessel layers so it never hides own
 * ship, AIS, the route or a plotted fix.
 *
 * Online-only: these are live tiles from tiles.openseamap.org with no offline
 * pack. Off-grid the layer simply shows nothing.
 *
 * The layer primitive here (setSeamarkOverlay + firstNonBaseLayerId) is written
 * to be reused by a future standalone OSM base+seamark provider (option B): B just
 * supplies its own base and calls setSeamarkOverlay to stamp the same marks on top.
 */
import type { Map as MaplibreMap } from 'maplibre-gl';

const SRC = 'openseamap-seamark';
const LYR = 'openseamap-seamark';

/** XYZ template for the transparent seamark tiles. */
export const SEAMARK_TILE_URL = 'https://tiles.openseamap.org/seamark/{z}/{x}/{y}.png';
/** The seamark tiles top out around z18; overzoom beyond that. */
export const SEAMARK_MAXZOOM = 18;
/** Required OSM/OpenSeaMap credit; surfaced both in the map credit chip and the sheet. */
export const SEAMARK_ATTRIBUTION = '© OpenSeaMap contributors, © OpenStreetMap contributors';

/**
 * The id of the first NON-base layer, so an overlay can be inserted directly on
 * top of the base/chart but beneath every app overlay. Base layers are the global
 * imagery basemap, the raster scan, and the vector cells; everything else
 * (ownship, ais, route, marks, plot/objects, safety, tide, pilotage, measure, …)
 * is an app overlay that must stay on top. Returns undefined when only base layers
 * exist, which puts the overlay at the top of the stack (still above the base).
 */
export function firstNonBaseLayerId(map: MaplibreMap): string | undefined {
  const isBase = (id: string) =>
    id === 'background' ||
    id === 'tile-basemap' ||
    id.startsWith('raster-chart') ||
    id.startsWith('cell-') ||
    id.startsWith('mtbase-') ||
    // The rough fallback locator base is base too. Without this the seamarks would
    // insert beneath its water background, which paints the whole viewport and
    // would hide them, the same way it was hiding the raster charts.
    id.startsWith('fbbase-');
  const layers = map.getStyle()?.layers ?? [];
  for (const l of layers) {
    if (!isBase(l.id)) return l.id;
  }
  return undefined;
}

/**
 * Show or hide the seamark overlay. Idempotent: when it's already up, it just
 * re-seats the layer under the current first app-overlay (the base/overlay set can
 * change when the provider or satellite toggle changes), moving it without tearing
 * the tiles down so there's no refetch flicker.
 */
export function setSeamarkOverlay(map: MaplibreMap, on: boolean, tilesUrl: string = SEAMARK_TILE_URL): void {
  const hasLayer = !!map.getLayer(LYR);
  if (!on) {
    if (hasLayer) map.removeLayer(LYR);
    if (map.getSource(SRC)) map.removeSource(SRC);
    return;
  }
  const before = firstNonBaseLayerId(map);
  if (hasLayer) {
    map.moveLayer(LYR, before);
    return;
  }
  if (!map.getSource(SRC)) {
    map.addSource(SRC, {
      type: 'raster',
      tiles: [tilesUrl],
      tileSize: 256,
      maxzoom: SEAMARK_MAXZOOM,
      attribution: SEAMARK_ATTRIBUTION,
    });
  }
  map.addLayer(
    { id: LYR, type: 'raster', source: SRC, paint: { 'raster-fade-duration': 150 } },
    before,
  );
}
